/**
 * A quién se emite una factura a huéspedes.
 *
 * Por defecto es el huésped que hizo el check-in, pero el cliente puede pedir
 * la factura a otro nombre (su empresa, otra persona) y dar sus propios datos
 * de facturación; entonces mandan esos, que son los que van al PDF y a Holded.
 *
 * Misma regla que `invoiceRecipient` en server/src/services/holded.ts — si
 * cambia una, cambia la otra.
 */

export interface BillingOverride {
  billingName?: string;
  billingNifType?: string;   // DNI / NIE / CIF / Pasaporte
  billingNif?: string;
  billingAddress?: string;
  billingEmail?: string;
  billingIsCompany?: boolean;
}

export interface InvoiceRecipient {
  name: string;
  document: string;
  documentType: string;
  address: string;
  email?: string;
  isCompany: boolean;
  /** true cuando los datos los dio el cliente y no vienen del check-in */
  isCustom: boolean;
}

const trim = (v: unknown) => String(v ?? '').trim();

/** Basta con el nombre o el NIF: a veces el cliente solo cambia uno de los dos. */
export function hasCustomBilling(inv: BillingOverride | null | undefined): boolean {
  return Boolean(inv && (trim(inv.billingName) || trim(inv.billingNif)));
}

export function invoiceRecipient(inv: any): InvoiceRecipient {
  if (!hasCustomBilling(inv)) {
    return {
      name: trim(inv?.guestName),
      document: trim(inv?.guestDocument),
      documentType: trim(inv?.guestDocumentType) || 'DNI',
      address: trim(inv?.guestAddress),
      isCompany: false,
      isCustom: false,
    };
  }

  return {
    name: trim(inv.billingName) || trim(inv.guestName),
    document: trim(inv.billingNif),
    documentType: trim(inv.billingNifType) || (inv.billingIsCompany ? 'CIF' : 'DNI'),
    address: trim(inv.billingAddress),
    email: trim(inv.billingEmail) || undefined,
    isCompany: Boolean(inv.billingIsCompany),
    isCustom: true,
  };
}
